import { defineStore } from 'pinia'
import { ref, computed, readonly } from 'vue'
import { apolloClient } from '@/lib/apollo'
import { LOGIN_MUTATION, ME_QUERY } from '@/graphql/auth'
import { connectPermissionsSocket, disconnectPermissionsSocket } from '@/lib/permissions-socket'

export interface User {
  id: string
  name: string
  email?: string
  permission: 'USER' | 'ADMIN'
  photoUrl?: string | null
  roles: Array<{
    id: number
    name: string
  }>
}

export const useAuthStore = defineStore('auth', () => {
  const user = ref<User | null>(JSON.parse(localStorage.getItem('auth-user') || 'null'))
  const token = ref<string | null>(localStorage.getItem('auth-token'))
  const loading = ref(false)
  const error = ref<string | null>(null)
  const initialized = ref(false)

  const isAuthenticated = computed(() => !!token.value && !!user.value)
  const isAdmin = computed(() => user.value?.permission === 'ADMIN')

  // Clear invalid tokens
  const clearAuth = () => {
    disconnectPermissionsSocket()
    user.value = null
    token.value = null
    localStorage.removeItem('auth-token')
    localStorage.removeItem('auth-user')
    error.value = null
  }

  async function login(username: string, password: string) {
    try {
      loading.value = true
      error.value = null

      const { data } = await apolloClient.mutate({
        mutation: LOGIN_MUTATION,
        variables: { input: { username, password } }
      })

      if (data?.login) {
        token.value = data.login.token
        user.value = data.login.employee
        localStorage.setItem('auth-token', data.login.token)
        localStorage.setItem('auth-user', JSON.stringify(data.login.employee))
        connectPermissionsSocket()
      }
    } catch (err: any) {
      error.value = err.message || 'Erreur de connexion'
      throw err
    } finally {
      loading.value = false
    }
  }

  async function logout() {
    disconnectPermissionsSocket()
    clearAuth()
    await apolloClient.clearStore()
    
    // Réinitialiser le store permissions
    const { usePermissionsStore } = await import('./permissions')
    const permissionsStore = usePermissionsStore()
    permissionsStore.reset()
    const { useRunningTimerStore } = await import('./runningTimer')
    useRunningTimerStore().reset()
    
    // Rediriger vers login sera géré par le routeur
  }

  async function checkAuth() {
    if (!token.value) {
      console.log('No token found in checkAuth')
      initialized.value = true
      return false
    }

    try {
      loading.value = true
      console.log('Checking auth with token:', token.value?.substring(0, 20) + '...')
      
      const { data, errors } = await apolloClient.query({
        query: ME_QUERY,
        fetchPolicy: 'network-only'
      })

      console.log('ME_QUERY response:', { data, errors })

      if (data?.me) {
        user.value = data.me
        localStorage.setItem('auth-user', JSON.stringify(data.me))
        console.log('Auth check successful, user:', user.value)
        
        // Charger les permissions du menu pour cet utilisateur
        const { usePermissionsStore } = await import('./permissions')
        const permissionsStore = usePermissionsStore()
        await permissionsStore.fetchMenuItems()
        connectPermissionsSocket()
        
        initialized.value = true
        return true
      } else {
        console.warn('No user data in response, clearing auth')
        clearAuth()
        initialized.value = true
        return false
      }
    } catch (err) {
      console.error('Auth check failed:', err)
      // Le token est invalide ou expiré, le supprimer
      clearAuth()
      initialized.value = true
      return false
    } finally {
      loading.value = false
    }
  }

  return {
    user: readonly(user),
    token: readonly(token),
    loading: readonly(loading),
    error: readonly(error),
    initialized: readonly(initialized),
    isAuthenticated,
    isAdmin,
    login,
    logout,
    checkAuth,
    clearAuth
  }
})